import { createHashHistory, HashHistory } from "history";
import { createReduxHistoryContext } from "redux-first-history";

/**
 * Create a safe wrapper for history to handle Office Add-in limitations.
 * 
 * In Office iframe context, window.history methods may be restricted or unavailable.
 * The error "p.replaceState is not a function" comes from redux-first-history
 * trying to call methods that don't exist in restricted contexts.
 * 
 * This wrapper intercepts history operations and provides fallbacks.
 */
const createSafeHistory = (history: HashHistory): HashHistory => {
  // Store original methods with proper binding
  const originalPush = history.push.bind(history);
  const originalReplace = history.replace.bind(history);
  const originalGo = history.go ? history.go.bind(history) : undefined;
  const originalBack = history.back ? history.back.bind(history) : undefined;
  const originalForward = history.forward ? history.forward.bind(history) : undefined;

  /**
   * Check if we're in a restricted context (like Office Add-in iframe)
   * where window.history methods may not be available
   */
  const isRestrictedContext = (): boolean => {
    try {
      // Test if window.history methods are available
      if (!window.history) return true;
      if (typeof window.history.pushState !== 'function') return true;
      if (typeof window.history.replaceState !== 'function') return true;
      return false;
    } catch {
      return true;
    }
  };

  /**
   * Fallback for changing location via hash when history API is unavailable
   */
  const hashFallback = (location: any): void => {
    try {
      if (typeof location === 'string') {
        const newHash = location.startsWith('#') ? location.substring(1) : location;
        window.location.hash = newHash;
      } else if (location && typeof location === 'object') {
        const path = location.pathname || '';
        const search = location.search || '';
        const hash = location.hash || '';
        window.location.hash = `${path}${search}${hash}`;
      }
    } catch (fallbackError) {
      console.error('[History] Even hash fallback failed:', fallbackError);
    }
  };

  /**
   * Safe wrapper for history.push
   */
  const safePush: HashHistory['push'] = (to: any, state?: any): void => {
    try {
      return originalPush(to, state);
    } catch (error) {
      console.warn('[History] Push failed in restricted context, using hash fallback:', error);
      if (isRestrictedContext()) {
        hashFallback(to);
      } else {
        // Re-throw if not in restricted context - might be a real error
        throw error;
      }
    }
  };

  /**
   * Safe wrapper for history.replace
   */
  const safeReplace: HashHistory['replace'] = (to: any, state?: any): void => {
    try {
      return originalReplace(to, state);
    } catch (error) {
      console.warn('[History] Replace failed in restricted context, using hash fallback:', error);
      if (isRestrictedContext()) {
        hashFallback(to);
      } else {
        // Re-throw if not in restricted context - might be a real error
        throw error;
      }
    }
  };

  // Create wrapped history object
  const wrappedHistory = {
    ...history,
    push: safePush,
    replace: safeReplace,
  };

  // Override navigation methods if they exist
  if (originalGo) {
    wrappedHistory.go = (delta: number): void => {
      try {
        return originalGo(delta);
      } catch (error) {
        console.warn('[History] Go failed in restricted context:', error);
        if (!isRestrictedContext()) throw error;
      }
    };
  }

  if (originalBack) {
    wrappedHistory.back = (): void => {
      try {
        return originalBack();
      } catch (error) {
        console.warn('[History] Back failed in restricted context:', error);
        if (!isRestrictedContext()) throw error;
      }
    };
  }

  if (originalForward) {
    wrappedHistory.forward = (): void => {
      try {
        return originalForward();
      } catch (error) {
        console.warn('[History] Forward failed in restricted context:', error);
        if (!isRestrictedContext()) throw error;
      }
    };
  }

  return wrappedHistory as HashHistory;
};

// office addins require HashRouter
const baseHistory = createHashHistory();

export const routerHistory = createSafeHistory(baseHistory);

export const { createReduxHistory, routerMiddleware, routerReducer } =
  createReduxHistoryContext({
    history: routerHistory,
  });

if (process.env.NODE_ENV === "development") {
  routerHistory.listen((update) => {
    console.debug(`[History] ${update.action}:`, update.location);
  });
}

// clone for easy coding
export default routerHistory;

